import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Upload, Send } from "lucide-react";

export default function FeedbacksPage() {
  const [imported, setImported] = useState([]);
  const [selected, setSelected] = useState([]);
  const [batch, setBatch] = useState("");
  const [draft, setDraft] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [campaign, setCampaign] = useState(null);
  const getError = (error) => error?.response?.data?.detail || error.message || "Ocorreu um erro";
  const loadImported = async () => {
    try { const { data } = await api.get("/imported-leads"); setImported(data); }
    catch (error) { setNotice(getError(error)); }
  };
  useEffect(() => {
    loadImported();
    api.get("/imported-leads/campaigns/recent").then(({ data }) => { if (data) setCampaign(data); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!campaign || campaign.status !== "running") return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/imported-leads/send/${campaign.id}`);
        setCampaign(data);
        if (data.status !== "running") loadImported();
      } catch (error) { setNotice(getError(error)); }
    }, 2500);
    return () => clearInterval(timer);
  }, [campaign?.id, campaign?.status]);
  const visible = batch ? imported.filter((lead) => lead.batch_id === batch) : imported;
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("Não foi possível ler a planilha"));
    reader.readAsDataURL(file);
  });
  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setNotice("");
    try {
      const { data } = await api.post("/imported-leads/import", { filename: file.name, data: await fileToBase64(file) });
      setBatch(data.batch_id);
      setSelected([]);
      setNotice(`Planilha importada: ${data.added} novos, ${data.updated} atualizados, ${data.rejected_count} linhas inválidas${data.rejected_lines.length ? ` (linhas ${data.rejected_lines.join(", ")})` : ""}.`);
      await loadImported();
    } catch (error) { setNotice(getError(error)); }
    finally { setBusy(false); }
  };
  const startSending = async () => {
    if (!selected.length || !draft.trim() || !authorized) { setNotice("Selecione os contatos, escreva a mensagem e confirme que eles podem ser contatados."); return; }
    if (!window.confirm(`Enviar esta mensagem para ${selected.length} contato(s) selecionado(s) pelo WhatsApp deste login? Confira o texto e os números antes de continuar.`)) return;
    setBusy(true);
    setNotice("");
    try {
      const { data } = await api.post("/imported-leads/send", { lead_ids: selected, text: draft.trim(), authorized });
      setCampaign({ id: data.id, total: data.total, sent: 0, failed: 0, skipped: 0, status: "running" });
      setNotice("Envio iniciado. Acompanhe o resultado abaixo.");
    } catch (error) { setNotice(getError(error)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Leads</h1>
        <p className="text-slate-400 mt-2">Acompanhe seus leads e atendimentos recebidos via WhatsApp.</p>
      </div>

      <Card className="bg-[#111827] border-slate-800 p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold">Planilhas de clientes</h2>
            <p className="text-sm text-slate-400">Importe quantas planilhas precisar: .xlsx ou .csv com colunas Nome, CPF e Telefone. Até 2.000 linhas por arquivo.</p>
          </div>
          <label className="cursor-pointer inline-flex gap-2 items-center rounded-lg bg-pink-600 px-4 py-2 font-semibold text-white">
            <Upload size={16} /> {busy ? "Aguarde..." : "Importar planilha"}
            <input type="file" accept=".xlsx,.csv" disabled={busy || campaign?.status === "running"} className="hidden" onChange={(event) => { importFile(event.target.files?.[0]); event.target.value = ""; }} />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-slate-300">Exibir:
            <select value={batch} onChange={(event) => { setBatch(event.target.value); setSelected([]); }} className="ml-2 bg-slate-900 border border-slate-700 rounded p-2">
              <option value="">Todos os importados</option>
              {[...new Set(imported.map((lead) => lead.batch_id).filter(Boolean))].map((id) => <option key={id} value={id}>Planilha {id}</option>)}
            </select>
          </label>
          <span className="text-sm text-slate-400">{visible.length} contatos · {selected.length} selecionados</span>
          <button type="button" className="text-pink-400 underline text-sm" onClick={() => setSelected(visible.slice(0, 20).map((lead) => lead.id))}>Selecionar até 20</button>
          <button type="button" className="text-slate-400 underline text-sm" onClick={() => setSelected([])}>Limpar seleção</button>
        </div>
        {notice && <p role="status" className="text-sm text-pink-300">{notice}</p>}
        <div className="max-h-64 overflow-auto border border-slate-800 rounded-lg">
          {visible.map((lead) => (
            <label key={lead.id} className="flex gap-3 items-center p-3 border-b border-slate-800 text-sm cursor-pointer">
              <input type="checkbox" checked={selected.includes(lead.id)} disabled={campaign?.status === "running"} onChange={(event) => setSelected((previous) => event.target.checked ? [...previous, lead.id].slice(0, 20) : previous.filter((id) => id !== lead.id))} />
              <span className="font-medium">{lead.name}</span><span className="text-slate-400">+{lead.phone}</span><span className="text-slate-500">CPF final {lead.cpf_final}</span>
            </label>
          ))}
          {!visible.length && <p className="p-4 text-slate-400 text-sm">Nenhuma planilha importada nesta seleção.</p>}
        </div>
        <div className="space-y-2">
          <label htmlFor="lead-message" className="text-sm font-medium">Mensagem para os selecionados (use {'{nome}'} para o primeiro nome)</label>
          <textarea id="lead-message" className="w-full min-h-24 bg-slate-900 border border-slate-700 rounded-lg p-3 text-white" maxLength={1500} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Olá, {nome}! ..." />
          <label className="flex gap-2 items-center text-sm text-slate-300"><input type="checkbox" checked={authorized} onChange={(event) => setAuthorized(event.target.checked)} /> Confirmo que estes contatos podem receber esta mensagem.</label>
          <button type="button" disabled={busy || campaign?.status === "running" || !selected.length || !draft.trim() || !authorized} className="inline-flex gap-2 items-center rounded-lg bg-pink-600 px-4 py-2 font-semibold text-white disabled:opacity-50" onClick={startSending}><Send size={16} /> Enviar para {selected.length} selecionado(s)</button>
          {campaign && <p role="status" className="text-sm text-slate-300">Envio {campaign.status === "running" ? "em andamento" : campaign.status}: {campaign.sent || 0} enviados, {campaign.failed || 0} falhas, {campaign.skipped || 0} bloqueados; total {campaign.total}.</p>}
        </div>
      </Card>

    </div>
  );
}
